import { NoArgsVoidFunc, Nullable, arrayUnstrictShift, isObject, perf } from './utils';

import { NestedTypedEventEmitter3 } from './event-emitter';

import { WebCodecsVideoDecHandler, WebCodecsVideoDecSupport } from './webcodecs-vdec-handler';
import { WebCodecsAudioDecHandler } from './webcodecs-adec-handler';

import { ValueCollector } from './tools/valuecollector';

const DEFAULT_AUDIO_SCHEDULING_LATENCY_SECONDS = 0.05;

const MAX_VIDEO_RENDER_QUEUE_LENGTH = 5;
const MAX_VIDEO_RENDER_QUEUE_DURATION_US = 100000;
const VIDEO_RENDER_TOLERANCE = 1.5;

const AV_SYNC_CORRECTION_THRESHOLD_US = 0;

const STATS_EVENT_INTERVAL_MS = 1000;

interface MetadataEvent {
  eventType: 'onMetaData';
  onMetaData: {
    handlerName: string;
    message: object;
  }
}

interface OnGopCache {
  duration: number;
  bytesize: number;
  videoframes: number;
  audioframes: number;
}

export type PlayerEvents = {
  timeupdate: NoArgsVoidFunc
  error: (msg: string, err: Error) => void
  stats: (reset: boolean) => void
}

export interface WebCodecsPlayerChunk {
  payloadUnit: Uint8Array,
  dtsUs: number,
  ptoUs: number,
  isKeyframe: boolean,
  isCodecConfig: boolean
}

export type WebCodecsDecodeBuffer = Array<WebCodecsPlayerChunk>
export type WebCodecsRenderBuffer = Array<VideoFrame>

export interface WebCodecsPlayerConfig {
  audioSchedulingLatencySecs?: number
}

export const playerDefaults: Required<WebCodecsPlayerConfig> = {
  audioSchedulingLatencySecs: DEFAULT_AUDIO_SCHEDULING_LATENCY_SECONDS
}

export class WebCodecsPlayer extends NestedTypedEventEmitter3<PlayerEvents> {

  private _config: Required<WebCodecsPlayerConfig>;

  private _prerollDurationMs = 0;
  private _prerollFramesVideo = 0;
  private _prerollFramesAudio = 0;
  private _prerollFramesVideoRemaining = 0;
  private _prerollFramesAudioRemaining = 0;

  private _audioDecHandler: WebCodecsAudioDecHandler;
  private _videoDecHandler: WebCodecsVideoDecHandler;

  private _videoRenderQueue: WebCodecsRenderBuffer = [];
  private _videoRenderIface: Nullable<CanvasDrawImage> = null;
  private _videoRenderParams: [number, number] = [0, 0];

  private _isRenderLoopActive = false;

  private _avSyncValues = new ValueCollector(250);

  private _lastFrameClockUs = 0;
  private _lastFrameTimestampUs = 0;

  private _gotAudioSyncClock = false;

  constructor(config: WebCodecsPlayerConfig = {}) {
    super();

    // apply defaults first and override with current config
    this._config = Object.assign({}, playerDefaults, config);

    this._audioDecHandler = new WebCodecsAudioDecHandler(this._config.audioSchedulingLatencySecs);
    // assign underrun cb
    this._audioDecHandler.onBufferUnderrun = this._onAudioBufferUnderrun.bind(this);

    this._videoDecHandler = new WebCodecsVideoDecHandler(
      this._onVideoDecRenderFunc.bind(this),
      WebCodecsVideoDecSupport.AVC1
    );

    setInterval(() => {
      this.emit('stats', false)
    }, STATS_EVENT_INTERVAL_MS);
  }

  get audioCurrentTimeUs(): number {
    return this._audioDecHandler.currentTimeUs;
  }

  get videoCurrentTimeUs(): number {
    return this._lastFrameTimestampUs;
  }

  getPlayClockUs(): number {
    if (this._gotAudioSyncClock) {
      return this.audioCurrentTimeUs;
    }
    return this.videoCurrentTimeUs;
  }

  getAudioScheduledLengthUs() {
    return this._audioDecHandler.latestTimeUs - this._audioDecHandler.currentTimeUs;
  }

  setCanvasDrawImage(drawIface: CanvasDrawImage, width: number, height: number) {1
    this._videoRenderIface = drawIface;
    this._videoRenderParams = [width, height];
  }

  setVolume(gain: number) {
    this._audioDecHandler.gainParam.value = gain;
  }

  setPrerollInfo(prerollDurationMs: number, prerollFramesVideo: number, prerollFramesAudio: number) {
    this._prerollDurationMs = prerollDurationMs;
    this._prerollFramesVideo = this._prerollFramesVideoRemaining = prerollFramesVideo;
    this._prerollFramesAudio = this._prerollFramesAudioRemaining = prerollFramesAudio;
  }

  handleJsonMessage(data: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!isObject(data)) {
      throw new Error(`Unhandled JSON data (not Object type): ${data}`);
    }

    if(data.eventType === 'onMetaData') {
      const metadataEvent = <MetadataEvent> data;
      try {
        if(metadataEvent.onMetaData?.handlerName === 'onGopCache') {
          const onGopCache = <OnGopCache> metadataEvent.onMetaData.message;
          console.log('onGopCache', onGopCache);

          if(isNaN(onGopCache.duration) ||
             isNaN(onGopCache.videoframes) ||
             isNaN(onGopCache.audioframes)) {
            console.warn('got invalid onGopCache event (ignored):', onGopCache);
            return;
          }
          this.setPrerollInfo(onGopCache.duration, onGopCache.videoframes, onGopCache.audioframes);
        }
      } catch(err) {
        this.emit('error', 'Exception handling metadata event:', err as Error);
      }
    }
  }

  appendAudioBuffer(chunk: WebCodecsPlayerChunk) {

    if(chunk.isCodecConfig && !this._audioDecHandler.config) {
      this._audioDecHandler.decode(chunk);
      this._gotAudioSyncClock = true;
      return;
    }

    if(0 < this._prerollFramesAudioRemaining) {
      console.log('Skipping audio preroll');
      this._prerollFramesAudioRemaining--;
      return;
    }

    this._audioDecHandler.decode(chunk);
  }

  appendVideoBuffer(chunk: WebCodecsPlayerChunk) {
    this._videoDecHandler.decode(chunk);
  }

  RenderVideo = () => {
    this._isRenderLoopActive = false;

    const render = () => {
      if(!(this._videoRenderIface && this._videoRenderQueue.length > 0)) {
        return
      }

      const clockNowUs = perf.now() * 1000;
      const timestampNowUs = this._videoRenderQueue[0].timestamp;

      const playClockUs = this.getPlayClockUs();

      if (this._gotAudioSyncClock && Number.isFinite(playClockUs)) {
        const deltaUs = playClockUs - timestampNowUs;
        //console.debug(deltaUs)
        const vals = this._avSyncValues.add(deltaUs);
        //console.debug('current AV synch:', vals)
        // IMPORTANT: Compare needs to be negative, as that means video is ahead and needs break
        if (deltaUs <= -AV_SYNC_CORRECTION_THRESHOLD_US) {
          //console.debug('A/V SYNC CORRECTION RENDERING BREAK - delta-us:', deltaUs);
          return;
        }
      }

      if(1 > this._lastFrameClockUs || 1 > this._lastFrameTimestampUs) {
        this._lastFrameClockUs = clockNowUs;
        this._lastFrameTimestampUs = timestampNowUs;
      }

      const queueDurationUs = this._videoRenderQueue[this._videoRenderQueue.length-1].timestamp - this._lastFrameTimestampUs;

      if((queueDurationUs > MAX_VIDEO_RENDER_QUEUE_DURATION_US) ||
          (VIDEO_RENDER_TOLERANCE * (clockNowUs - this._lastFrameClockUs) >= timestampNowUs - this._lastFrameTimestampUs)) {
        const frame = arrayUnstrictShift(this._videoRenderQueue);
        const [w, h] = this._videoRenderParams;

        this._lastFrameClockUs = clockNowUs;
        this._lastFrameTimestampUs = timestampNowUs;

        this._videoRenderIface.drawImage(frame, 0, 0, w, h);
        frame.close();
      }

      while(this._videoRenderQueue.length > MAX_VIDEO_RENDER_QUEUE_LENGTH) {
        const frameDrop = arrayUnstrictShift(this._videoRenderQueue);
        frameDrop.close();
      }

    }

    try {
      render();
    } catch(err) {
      this.emit('error', 'Exception handling video rendering:', err as Error);
    } finally {

      requestAnimationFrame(this.RenderVideo);
      this._isRenderLoopActive = true;

      // decouple event from callstack to avoid side-effects in App-side handlers
      setTimeout(() => {
        this.emit('timeupdate');
      }, 0);
    }
  }

  private _onVideoDecRenderFunc(frame: VideoFrame) {

    if(0 < this._prerollFramesVideoRemaining) {
      console.log('Skipping video preroll');
      this._prerollFramesVideoRemaining--;
      frame.close();
      return;
    }

    if(document.visibilityState !== 'visible') {
      console.log('Skipping video frames in hidden state');
      frame.close();

      while(this._videoRenderQueue.length > 0) {
        const frameDrop = arrayUnstrictShift(this._videoRenderQueue);
        frameDrop.close();
      }

      return;
    }

    this._videoRenderQueue.push(frame);

    if(!this._isRenderLoopActive) {
      this.RenderVideo();
    }
  }

  private _onAudioBufferUnderrun() {
    console.warn('_onAudioBufferUnderrun')
    //this._isPlaying = false;
  }
}
